import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { isValidCSharpInt } from 'src/app/common-components/helpers/csharp-max-integer-helper';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { SaveSipUsers, SaveSipUsersVariables } from '../../device-register/queries/generated/SaveSipUsers';
import { SaveSipUserMutation } from '../../device-register/queries/save-sipuser.graphql';
import { ExtensionRegisterOptions } from '../models/extension-register-options';
import { ExtensionsFilterOption } from '../models/extensions-filter-option';
import { getExtensionNumbersQuery, getExtensionsForAddQuery, getExtensionsQuery } from '../queries/extensions.graphql';
import { GetExtensionIds, GetExtensionIdsVariables } from '../queries/generated/GetExtensionIds';
import { GetExtensionNumbers, GetExtensionNumbersVariables } from '../queries/generated/GetExtensionNumbers';
import { GetExtensions, GetExtensionsVariables } from '../queries/generated/GetExtensions';
import { GetExtensionsForAdd, GetExtensionsForAddVariables } from '../queries/generated/GetExtensionsForAdd';

@Injectable({ providedIn: 'root' })
export class ExtensionRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getExtensions(
      endCursor: string | null,
      filters: ExtensionsFilterOption[] | null,
      quickFilters: ExtensionRegisterOptions | null,
      orderingOptions: OrderingOption[] | null
   ): Observable<ExecutionResult<GetExtensions>> {
      const filterExpression = this.constructFilterExpression(filters, quickFilters);

      const variables: GetExtensionsVariables = {
         after: endCursor,
         filters: this.buildFilterExpression(filterExpression),
         first: 50,
         orderingOptions,
      };

      return this.apiService.query<GetExtensions, GetExtensionsVariables>({
         query: getExtensionsQuery,
         variables,
      });
   }

   getExtensionIds(
      endCursor: string | null,
      filters: ExtensionsFilterOption[] | null,
      quickFilters: ExtensionRegisterOptions | null,
      orderingOptions: OrderingOption[] | null
   ): Observable<ExecutionResult<GetExtensionIds>> {
      const filterExpression = this.constructFilterExpression(filters, quickFilters);

      const variables: GetExtensionsVariables = {
         after: endCursor,
         filters: this.buildFilterExpression(filterExpression),
         first: 50,
         orderingOptions,
      };

      return this.apiService.query<GetExtensionIds, GetExtensionIdsVariables>({
         query: getExtensionsQuery,
         variables,
      });
   }

   getExtensionsForAdd(
      endCursor: string | null,
      filters: ExtensionsFilterOption[] | null,
      quickFilters: ExtensionRegisterOptions | null,
      orderingOptions: OrderingOption[] | null,
      accountId: number | null
   ): Observable<ExecutionResult<GetExtensionsForAdd>> {
      const filterBuilderFilterExpression: FilterExpression = {
         filterOptions: filters?.map(this.convertExtensionFilterOption.bind(this)) ?? [],
      };

      filterBuilderFilterExpression.filterOptions.push({
         field: 'AccountId',
         operation: FilterOperations.EqualTo,
         value: accountId,
      });
      filterBuilderFilterExpression.filterOptions.push({
         field: 'IsArchived',
         operation: FilterOperations.EqualTo,
         value: false,
      });

      const filterBuilderFilterExpressionGroup: FilterExpression = {
         filterOptions: filters?.map(this.convertExtensionFilterOption.bind(this)) ?? [],
      };
      filterBuilderFilterExpressionGroup.filterOptions.push({
         field: 'AccountId',
         operation: FilterOperations.EqualTo,
         value: accountId,
      });

      const variables: GetExtensionsForAddVariables = {
         after: endCursor,
         filtersExtensions: this.buildFilterExpression(filterBuilderFilterExpression),
         filtersExtensionGroups: this.buildFilterExpression(filterBuilderFilterExpressionGroup),
         orderingOptions,
      };

      return this.apiService.query<GetExtensionsForAdd, GetExtensionsForAddVariables>({
         query: getExtensionsForAddQuery,
         variables,
      });
   }

   getExtensionNumbers(
      filters: ExtensionsFilterOption[] | null,
      accountId: number | null
   ): Observable<ExecutionResult<GetExtensionNumbers>> {
      const filterBuilderFilterExpression: FilterExpression = {
         filterOptions: filters?.map(this.convertExtensionFilterOption.bind(this)) ?? [],
      };

      filterBuilderFilterExpression.filterOptions.push({
         field: 'AccountId',
         operation: FilterOperations.EqualTo,
         value: accountId,
      });
      filterBuilderFilterExpression.filterOptions.push({
         field: 'IsArchived',
         operation: FilterOperations.EqualTo,
         value: false,
      });

      const variables: GetExtensionNumbersVariables = {
         after: null,
         filtersExtensions: this.buildFilterExpression(filterBuilderFilterExpression),
         orderingOptions: null,
      };

      return this.apiService.query<GetExtensionNumbers, GetExtensionNumbersVariables>({
         query: getExtensionNumbersQuery,
         variables,
      });
   }

   changeSipUserPasswords(sipUserIds: number[]): Observable<ExecutionResult<SaveSipUsers>> {
      const sipUsers = sipUserIds.map((sipUserId) => ({
         sipUserId,
         resetSipUserPassword: true,
         fields: ['resetSipUserPassword'],
      }));

      return this.apiService.query<SaveSipUsers, SaveSipUsersVariables>({
         query: SaveSipUserMutation,
         variables: {
            sipUsers,
         },
      });
   }

   private convertExtensionFilterOption(option: ExtensionsFilterOption): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: ExtensionsFilterOption): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'extension':
            return Array.isArray(option.value)
               ? option.value.map((v) => v.extensionTypeId)
               : option.value?.extensionTypeId || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
         default:
            return null;
      }
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }

   private constructFilterExpression(
      filters: ExtensionsFilterOption[] | null,
      quickFilters: ExtensionRegisterOptions | null
   ): FilterExpression {
      const filterBuilderFilterExpression: FilterExpression = {
         filterOptions: filters?.map(this.convertExtensionFilterOption.bind(this)) ?? [],
      };

      filterBuilderFilterExpression.filterOptions.push({
         field: 'IsArchived',
         operation: FilterOperations.EqualTo,
         value: false,
      });

      if (quickFilters) {
         if (quickFilters.extensionType) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'Type',
               operation: FilterOperations.EqualTo,
               value: quickFilters.extensionType.extensionTypeId,
            });
         }
         if (quickFilters.searchText) {
            const subExpression: FilterExpression = { filterOptions: [], logicalOperator: LogicalOperators.Or };
            subExpression.filterOptions.push({
               field: 'Name',
               operation: FilterOperations.Contains,
               value: quickFilters.searchText.trim(),
            });
            subExpression.filterOptions.push({
               field: 'ExtensionPhone.DefaultPhoneNumber.PhoneNumber',
               operation: FilterOperations.Contains,
               value: quickFilters.searchText.trim(),
            });
            const trimmedText = quickFilters.searchText.trim();
            if (trimmedText !== '' && isValidCSharpInt(+trimmedText)) {
               subExpression.logicalOperator = LogicalOperators.Or;
               subExpression.filterOptions.push({
                  field: 'Number',
                  operation: FilterOperations.Contains,
                  value: trimmedText,
               });
            }
            filterBuilderFilterExpression.subExpressions = [subExpression];
         }
         if (quickFilters.showExtensionsWithIssues) {
            const issueExpression: FilterExpression = {
               logicalOperator: LogicalOperators.Or,
               filterOptions: [
                  {
                     field: 'Phones.SipUsers.IsHacked',
                     operation: FilterOperations.EqualTo,
                     value: true,
                  },
                  {
                     field: 'Phones.SipUsers.IsFraud',
                     operation: FilterOperations.EqualTo,
                     value: true,
                  },
               ],
            };
            filterBuilderFilterExpression.subExpressions = filterBuilderFilterExpression.subExpressions
               ? [...filterBuilderFilterExpression.subExpressions, issueExpression]
               : [issueExpression];
         }
      }
      return filterBuilderFilterExpression;
   }
}
